import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { PROCOGIA_COLORS } from '../utils/themeUtils';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import { 
  Search, 
  Filter, 
  ChevronRight,
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  MapPin,
  Zap
} from 'lucide-react';

const EquipmentListPage = () => {
  const { getThemeClasses } = useTheme();
  const { user } = useAuth();
  const themeClasses = getThemeClasses();
  const [searchParams, setSearchParams] = useSearchParams();
  const [equipment, setEquipment] = useState([]);
  const [filteredEquipment, setFilteredEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    fetchEquipment();
  }, []);

  useEffect(() => {
    filterEquipment();
  }, [equipment, searchTerm, statusFilter, typeFilter]);

  const fetchEquipment = async () => {
    try {
      setLoading(true);
      const response = await axios.get('equipment');
      if (response.data && response.data.equipment) {
        setEquipment(response.data.equipment);
      }
    } catch (error) {
      console.error('Error fetching equipment:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterEquipment = () => {
    let filtered = equipment;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(eq => 
        eq.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        eq.equipmentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        eq.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(eq => eq.status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(eq => eq.type === typeFilter);
    }

    setFilteredEquipment(filtered);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'critical':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'maintenance':
        return <Clock className="h-5 w-5 text-blue-500" />;
      default:
        return <Activity className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational':
        return { 
          backgroundColor: '#e8f5e8', 
          color: PROCOGIA_COLORS.green 
        };
      case 'warning':
        return { 
          backgroundColor: '#fff3cd', 
          color: '#856404' 
        };
      case 'critical':
        return { 
          backgroundColor: '#f8d7da', 
          color: '#721c24' 
        };
      case 'maintenance':
        return { 
          backgroundColor: '#e6f7fa', 
          color: PROCOGIA_COLORS.blue 
        };
      default:
        return { 
          backgroundColor: '#f8f9fa', 
          color: PROCOGIA_COLORS.darkGrey 
        };
    }
  };

  const equipmentTypes = [...new Set(equipment.map(eq => eq.type))];

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${themeClasses.text.primary}`}>
            Equipment Management
          </h1>
          <p className={`mt-2 ${themeClasses.text.secondary}`}>
            Monitor and manage all manufacturing equipment across facilities
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${themeClasses.bg.accent} ${themeClasses.text.primary}`}>
            {filteredEquipment.length} of {equipment.length} equipment
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className={`${themeClasses.bg.card} rounded-lg shadow p-6`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search equipment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${themeClasses.bg.secondary} ${themeClasses.border.primary} ${themeClasses.text.primary}`}
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${themeClasses.bg.secondary} ${themeClasses.border.primary} ${themeClasses.text.primary}`}
          >
            <option value="all">All Status</option>
            <option value="operational">Operational</option>
            <option value="warning">Warning</option>
            <option value="critical">Critical</option>
            <option value="maintenance">Maintenance</option>
          </select>

          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${themeClasses.bg.secondary} ${themeClasses.border.primary} ${themeClasses.text.primary}`}
          >
            <option value="all">All Types</option>
            {equipmentTypes.map(type => (
              <option key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Equipment Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredEquipment.map((item) => (
          <div
            key={item.id}
            className={`${themeClasses.bg.card} rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer`}
            onClick={() => window.location.href = `/equipment/${item.id}`}
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 p-2 rounded-lg dark:bg-blue-900">
                    <Zap className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${themeClasses.text.primary}`}>
                      {item.name}
                    </h3>
                    <p className={`text-sm ${themeClasses.text.secondary}`}>
                      {item.equipmentId}
                    </p>
                  </div>
                </div>
                <ChevronRight className={`h-5 w-5 ${themeClasses.text.accent}`} />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${themeClasses.text.secondary}`}>Status</span>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(item.status)}
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={getStatusColor(item.status)}
                    >
                      {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className={`text-sm ${themeClasses.text.secondary}`}>Type</span>
                  <span className={`text-sm font-medium ${themeClasses.text.primary}`}>
                    {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                  </span>
                </div>

                <div className="flex items-start justify-between">
                  <span className={`text-sm ${themeClasses.text.secondary}`}>Location</span>
                  <div className="flex items-center space-x-1">
                    <MapPin className="h-3 w-3 text-gray-400" />
                    <span className={`text-sm ${themeClasses.text.primary} text-right`}>
                      {item.location}
                    </span>
                  </div>
                </div>

                {item.specifications && (
                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${themeClasses.text.secondary}`}>Power</span>
                      <span className={`text-sm font-medium ${themeClasses.text.primary}`}>
                        {item.specifications.power || 'N/A'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredEquipment.length === 0 && (
        <div className={`text-center py-12 ${themeClasses.bg.card} rounded-lg`}>
          <Activity className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className={`mt-2 text-sm font-medium ${themeClasses.text.primary}`}>No equipment found</h3>
          <p className={`mt-1 text-sm ${themeClasses.text.secondary}`}>
            Try adjusting your search or filter criteria.
          </p>
        </div>
      )}
    </div>
  );
};

export default EquipmentListPage; 